import React, { useState, useEffect } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { TiltCard } from './TiltCard';
import { ScrollDepthReveal } from './ScrollDepthReveal';

const BrandsSection = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredBrand, setHoveredBrand] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const r = await fetch(`${API_URL}/api/brands`);
        if (r.ok) {
          const data = await r.json();
          setBrands(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error('Failed to load brands', e);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, [API_URL]);

  return (
    <section className="relative py-20 lg:py-28 overflow-hidden bg-[#0a0a0c]">
      {/* Ambient Orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/[0.05] rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/[0.05] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <ScrollDepthReveal className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-white/5 border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-4">
            Trusted By Industry Leaders
          </span>
          <h2
            className="text-3xl lg:text-5xl font-bold bg-gradient-to-r from-white via-neutral-300 to-neutral-500 bg-clip-text text-transparent"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Brands Worked With
          </h2>
        </ScrollDepthReveal>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
          </div>
        ) : brands.length === 0 ? (
          <p className="text-center text-neutral-500">No brands to display.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 lg:gap-6">
            {brands.map((brand, index) => (
              <ScrollDepthReveal key={brand.brand_id} delay={Math.min(index * 0.06, 0.3)}>
                <TiltCard
                  maxTilt={18}
                  hoverScale={1.08}
                  testId={`brand-tilt-card-${index}`}
                  className="group"
                >
                  <div
                    onMouseEnter={() => setHoveredBrand(index)}
                    onMouseLeave={() => setHoveredBrand(null)}
                    className="relative"
                  >
                    {/* Glow Effect */}
                    <div className={`absolute -inset-1 bg-gradient-to-r ${brand.color} rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500`} />

                    {/* Card */}
                    <div className="relative h-36 lg:h-44 flex flex-col items-center justify-center bg-white/[0.04] backdrop-blur-xl rounded-2xl border border-white/10 p-4 overflow-hidden group-hover:border-white/30 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-black/50">
                      {/* Glass Reflection Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-2xl" />
                      <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-white/10 to-transparent rotate-12 transform group-hover:translate-x-full group-hover:translate-y-full transition-transform duration-1000" />

                      {/* Logo or Name */}
                      <div className="relative z-10 flex flex-col items-center justify-center h-full w-full" style={{ transform: 'translateZ(45px)' }}>
                        <img
                          src={brand.logo}
                          alt={brand.name}
                          className="max-h-20 lg:max-h-24 max-w-[90%] w-auto object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <span
                          className="hidden text-base font-bold text-neutral-400 group-hover:text-white transition-colors text-center"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {brand.name.split(' ').slice(0, 2).join(' ')}
                        </span>
                      </div>

                      {/* Hover Info Icon */}
                      <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <Info size={14} className="text-neutral-400" />
                      </div>
                    </div>

                    {/* Hover Info Tooltip — dark glass */}
                    {brand.description && (
                      <div className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-3 w-72 z-50 transition-all duration-300 ${hoveredBrand === index ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
                        <div className="relative bg-neutral-900/95 backdrop-blur-xl rounded-xl p-4 shadow-2xl border border-white/10 overflow-hidden">
                          {/* Brand-color accent strip on top */}
                          <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${brand.color}`} />
                          <h3 className="text-white font-bold text-sm mb-2 mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                            {brand.name}
                          </h3>
                          <p className="text-neutral-400 text-xs leading-relaxed">
                            {brand.description}
                          </p>
                          {/* Arrow */}
                          <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-4 h-4 bg-neutral-900/95 border-r border-b border-white/10 transform rotate-45" />
                        </div>
                      </div>
                    )}
                  </div>
                </TiltCard>
              </ScrollDepthReveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BrandsSection;
